import { Binding, SelectedFilters, Station } from '@/types/azk-binding.types';
import { BindingUpdateData } from '@/types//azk-binding.types';

export const getInfluenceTypeLabel = (
  influenceTypes: { value: string; label: string }[],
  value: string | undefined
): string => {
  if (!value) return '';
  const type = influenceTypes.find(t => t.value === value);
  return type ? type.label : value;
};

export const getLocationTypeLabel = (
  locationTypes: { value: string; label: string }[],
  value: string | undefined
): string => {
  if (!value) return '';
  const type = locationTypes.find(t => t.value === value);
  return type ? type.label : value;
};

export const getCleanedFilters = (filters: SelectedFilters) => {
  return Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== 'Всі'));
};

export const getNextFilters = (
  prev: SelectedFilters,
  filterType: keyof SelectedFilters,
  value: string
): SelectedFilters => {
  if (filterType === 'region') {
    return { [filterType]: value } as SelectedFilters;
  }

  if (filterType === 'province') {
    return {
      ...prev,
      [filterType]: value,
      brand: 'Всі'
    };
  }

  return {
    ...prev,
    [filterType]: value
  };
};

export const defaultSelectedFilters: SelectedFilters = {
  region: 'Всі',
  province: 'Всі',
  brand: 'Всі'
};

export const extractSettlement = (station: Station, address: string): string => {
  if (station.settlement_name) {
    return String(station.settlement_name).trim();
  }
  if (!address) return '';

  address = address.trim();

  if (address.startsWith('селище')) {
    const words = address.split(/\s+/);
    return words.length > 1 ? words[1] : words[0] || '';
  }

  const dotIndex = address.indexOf('.');
  if (dotIndex !== -1) {
    const afterDot = address.substring(dotIndex + 1).trim();
    const spaceIndex = afterDot.indexOf(' ');
    return spaceIndex !== -1 ? afterDot.substring(0, spaceIndex) : afterDot;
  }

  const words = address.split(/\s+/);
  return words.length > 1 ? words[1] : words[0] || '';
};

export const sortStations = (stations: Station[], sortField: string, sortDirection: 'asc' | 'desc') => {
  if (!sortField) return stations;

  return [...stations].sort((a, b) => {
    let aValue: any = (a as any)[sortField];
    let bValue: any = (b as any)[sortField];

    if (sortField === 'station_number') {
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    } else if (sortField === 'address') {
      aValue = String(aValue || '');
      bValue = String(bValue || '');
      const aSettlement = extractSettlement(a, aValue);
      const bSettlement = extractSettlement(b, bValue);
      const comparison = aSettlement.localeCompare(bSettlement, 'uk-UA');
      return sortDirection === 'asc' ? comparison : -comparison;
    } else {
      aValue = String(aValue || '');
      bValue = String(bValue || '');
      const comparison = aValue.localeCompare(bValue, 'uk-UA');
      return sortDirection === 'asc' ? comparison : -comparison;
    }
  });
};

export const getOkkoBindingsForCompetitor2 = (
  selectedCompetitor: Station | null,
  bindings: Binding[]
) => {
  if (!selectedCompetitor) return [];

  const competitorBindings = bindings.filter(b => b.competitor_station_unicode === selectedCompetitor.unicode);

  return competitorBindings.map(binding => ({
    okkoStationUnicode: binding.okko_station_unicode,
    okkoStation: {
      unicode: binding.okko_station_unicode,
      address: binding.okko_station_address,
      name: binding.okko_station_name,
      brand: binding.okko_brand_name
    },
    binding
  }));
};

export const getOkkoBindingDetails = (
  selectedOkko: Station | null,
  bindings: Binding[],
  allCompetitorStations: Station[]
) => {
  const details = [];

  if (selectedOkko) {
    const firstBinding = bindings[0];
    details.push({
      ...selectedOkko,
      address: selectedOkko.address || firstBinding?.okko_station_address || '',
      brand: selectedOkko.brand || firstBinding?.okko_brand_name || '',
      name: selectedOkko.name || firstBinding?.okko_station_name || '',
      isOkko: true,
      influence_type: '',
      location_type: '',
      distance_to_azk: 0,
      straight_distance: 0
    });

    bindings.forEach(binding => {
      const competitor = allCompetitorStations.find(s => s.unicode === binding.competitor_station_unicode) || {
        unicode: binding.competitor_station_unicode,
        address: binding.competitor_station_address,
        name: binding.competitor_station_name,
        brand: binding.competitor_brand_name
      };
      details.push({
        ...competitor,
        ...binding,
        isOkko: false
      });
    });
  }

  return details;
};
export const normalizeDistance = (distance: string | number | undefined): number => {
  if (distance === undefined || distance === null || distance === '') return 0;
  const normalized = String(distance).replace(',', '.').trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isBindingEqualToOriginal = (current: Binding, original: Binding): boolean => {
  return (
    (current.influence_type || '') === (original.influence_type || '') &&
    (current.location_type || '') === (original.location_type || '') &&
    normalizeDistance(current.distance_to_azk) === normalizeDistance(original.distance_to_azk)
  );
};

export const getHasChanges = (bindings: Binding[]): boolean => {
  return bindings.map(binding => binding.status).some(status => status !== 'unchanged');
};

export const getNextUpdatedItems = (
  bindingsOkko: Binding[],
  okkoBindings: Binding[] | undefined,
  bindingId: number,
  field: string,
  value: unknown
): Binding[] | null => {
  const itemToUpdate = bindingsOkko.find(item => item.id === bindingId);
  if (!itemToUpdate || itemToUpdate.status === 'deleted' || !okkoBindings) return null;

  const updatedItem = { ...itemToUpdate, [field]: value } as Binding;
  const originalItem = okkoBindings.find(item => item.id === bindingId);
  const isChanged = originalItem ? !isBindingEqualToOriginal(updatedItem, originalItem) : true;

  return bindingsOkko.map(item =>
    item.id === bindingId ? { ...item, [field]: value, status: isChanged ? 'edited' : 'unchanged' } : item
  );
};

export const getNextItemsAfterDeletion = (bindingsOkko: Binding[], bindingId: number): Binding[] => {
  return bindingsOkko.flatMap(item => {
    if (item.id !== bindingId) return [item];
    if (item.type === 'new') return [];

    return [{ ...item, status: 'deleted' }];
  });
};

export const getNextItemsAfterAdd = (
  bindingsOkko: Binding[],
  selectedOkko: Station,
  competitor: Station
): { nextItems: Binding[] | null; alreadyExists: boolean } => {
  const existingBinding = bindingsOkko.find(
    binding =>
      binding.okko_station_unicode === selectedOkko.unicode &&
      binding.competitor_station_unicode === competitor.unicode &&
      !bindingsOkko
        .filter(item => item.status === 'deleted')
        .map(item => item.id)
        .includes(binding.id)
  );

  if (existingBinding) {
    return { nextItems: null, alreadyExists: true };
  }

  const newBinding = createNewOkkoBindingDraft(selectedOkko, competitor);
  if (!newBinding) {
    return { nextItems: null, alreadyExists: false };
  }

  return {
    nextItems: [...bindingsOkko, { ...newBinding, status: 'added', isOkko: false }],
    alreadyExists: false
  };
};

export const buildBulkRequest = (
  okkoUnicode: number,
  remainingBindings: Binding[],
  parseDistance: (v: string | number | undefined) => number,
  influenceTypes: { value: string; label: string }[] = [],
  locationTypes: { value: string; label: string }[] = []
): { okkoStationUnicode: number; bindings: BindingUpdateData[] } => {
  const resolveId = (types: { value: string; label: string }[], val: string | undefined): number => {
    if (!val) return 0;
    // Already a numeric ID
    if (!isNaN(Number(val))) return Number(val);
    // String label — look up by label
    return Number(types.find(t => t.label === val)?.value || 0);
  };

  return {
    okkoStationUnicode: okkoUnicode,
    bindings: remainingBindings.map(binding => {
      const bindingData: BindingUpdateData = {
        competitorStationUnicode: Number(binding.competitor_station_unicode || binding.competitorStationUnicode),
        influence_type_id: resolveId(influenceTypes, binding.influence_type || binding.influenceType) || undefined,
        location_type_id: resolveId(locationTypes, binding.location_type || binding.locationType) || undefined,
        distance_to_azk: parseDistance(binding.distance_to_azk || binding.distance || binding.manual_distance) || undefined
      };

      if (binding.type !== 'new') {
        bindingData.id = Number(binding.id);
      }

      return bindingData;
    })
  };
};

export const createNewOkkoBindingDraft = (
  selectedOkko: Station,
  competitor: Station
): (Binding & { type: 'new' }) | null => {
  const nowIso = new Date().toISOString();

  if (selectedOkko.unicode === undefined || competitor.unicode === undefined) return null;

  return {
    id: Date.now() + Math.random(),
    okko_station_unicode: selectedOkko.unicode,
    competitor_station_unicode: competitor.unicode,
    influence_type: '',
    location_type: '',
    distance_to_azk: 0,
    straight_distance: 0,
    okko_station_name: selectedOkko.name || '',
    okko_station_address: selectedOkko.address || '',
    competitor_station_name: competitor.name || '',
    competitor_station_address: competitor.address || '',
    okko_brand_name: 'ОККО',
    competitor_brand_name: competitor.brand || '',
    type: 'new',
    created_at: nowIso,
    updated_at: nowIso
  };
};
